import { LuIcon, Menu, type MenuRootProps, type MenuVariantProps } from "@overmap-ai/blocks"
import type { AssetTypeIdentifier, AssetTypeStatus, ProcedureStep, Stored, User } from "@overmap-ai/common"
import { ConditionManager, type Field, FieldSchema } from "@overmap-ai/forms"
import { Eye } from "lucide-react"
import { Fragment, memo, type ReactElement, useCallback, useMemo } from "react"

import { AssetLabelFilterDef } from "../../filtering/defs/AssetLabelFilterDef"
import { AssetTypeStatusFilterDef } from "../../filtering/defs/AssetTypeStatusFilterDef"
import { IdentifierFilterDef } from "../../filtering/defs/IdentifierFilterDef"
import { ProcedureStepAssigneeFilterDef } from "../../filtering/defs/ProcedureStepAssigneeFilterDef"
import { ProcedureStepCompletionStatusFilterDef } from "../../filtering/defs/ProcedureStepCompletionStatusFilterDef"
import { ProcedureStepReviewerFilterDef } from "../../filtering/defs/ProcedureStepReviewerFilterDef"
import { ProcedureStepReviewStatusFilterDef } from "../../filtering/defs/ProcedureStepReviewStatusFilterDef"
import { SubmittedAtFilterDef } from "../../filtering/defs/SubmittedAtFilterDef"
import { getFilterDefOfField } from "../../filtering/defs/utils"
import { FilterManager } from "../../filtering/manager"
import { modelIconMapping } from "../../mappings/icons"
import type { AssetData, ProcedureData } from "../../typings"
import { FieldGroup } from "../MenuGroups/FieldGroup"
import { IdentifierGroup } from "../MenuGroups/IdentifierGroup"

export interface AssetDataMapFilterMenuProps extends MenuRootProps, MenuVariantProps {
	children: ReactElement
	// Asset filtering
	assetDataFilterManager: FilterManager<AssetData>
	assetTypeIdentifiers: Stored<AssetTypeIdentifier>[]
	assetTypeFieldSchema: FieldSchema
	assetTypeStatuses: Stored<AssetTypeStatus>[]
	// Procedure filtering
	procedureDataFilterManager?: FilterManager<ProcedureData>
	procedureTypeFieldSchema?: FieldSchema
	procedureSteps?: Stored<ProcedureStep>[]
	procedureStepFieldSchemasAndConditions?: Record<ProcedureStep["uuid"], [FieldSchema, ConditionManager]>
	users?: User[]
}

export const AssetDataMapFilterMenu = memo<AssetDataMapFilterMenuProps>((props) => {
	const {
		children,
		variant,
		size,
		accentColor,
		// Asset filtering
		assetDataFilterManager,
		assetTypeIdentifiers,
		assetTypeFieldSchema,
		assetTypeStatuses,
		// Procedure filtering
		procedureDataFilterManager,
		procedureTypeFieldSchema,
		procedureSteps,
		procedureStepFieldSchemasAndConditions,
		users,
		...rest
	} = props

	const handleSelectLabel = useCallback(() => {
		assetDataFilterManager.addFilter(new AssetLabelFilterDef().createFilter())
	}, [assetDataFilterManager])

	const handleSelectSubmittedAt = useCallback(() => {
		assetDataFilterManager.addFilter(new SubmittedAtFilterDef().createFilter())
	}, [assetDataFilterManager])

	const handleSelectStatus = useCallback(() => {
		if (!assetTypeStatuses) return

		assetDataFilterManager.addFilter(new AssetTypeStatusFilterDef(assetTypeStatuses).createFilter())
	}, [assetTypeStatuses, assetDataFilterManager])

	const handleIdentifierSelect = useCallback(
		(identifier: Stored<AssetTypeIdentifier>) => {
			assetDataFilterManager.addFilter(new IdentifierFilterDef(identifier).createFilter())
		},
		[assetDataFilterManager],
	)

	const handleAssetTypeFieldSelect = useCallback(
		(field: Field) => {
			assetDataFilterManager.addFilter(getFilterDefOfField(field).createFilter())
		},
		[assetDataFilterManager],
	)

	const supportedAssetTypeFields = useMemo(() => assetTypeFieldSchema?.getFields(), [assetTypeFieldSchema])

	const handleSelectProcedureStepCompletionStatus = useCallback(
		(procedureStep: Stored<ProcedureStep>) => {
			if (!procedureDataFilterManager) return

			procedureDataFilterManager.addFilter(
				new ProcedureStepCompletionStatusFilterDef(procedureStep).createFilter(),
			)
		},
		[procedureDataFilterManager],
	)

	const handleSelectProcedureStepReviewStatus = useCallback(
		(procedureStep: Stored<ProcedureStep>) => {
			if (!procedureDataFilterManager) return

			procedureDataFilterManager.addFilter(new ProcedureStepReviewStatusFilterDef(procedureStep).createFilter())
		},
		[procedureDataFilterManager],
	)

	const handleSelectProcedureStepAssignees = useCallback(
		(procedureStep: Stored<ProcedureStep>) => {
			if (!procedureDataFilterManager || !users) return

			procedureDataFilterManager.addFilter(
				new ProcedureStepAssigneeFilterDef(procedureStep, users).createFilter(),
			)
		},
		[procedureDataFilterManager, users],
	)

	const handleSelectProcedureStepReviewers = useCallback(
		(procedureStep: Stored<ProcedureStep>) => {
			if (!procedureDataFilterManager || !users) return

			procedureDataFilterManager.addFilter(
				new ProcedureStepReviewerFilterDef(procedureStep, users).createFilter(),
			)
		},
		[procedureDataFilterManager, users],
	)

	const handleProcedureTypeFieldSelect = useCallback(
		(field: Field) => {
			if (!procedureDataFilterManager) return

			procedureDataFilterManager.addFilter(getFilterDefOfField(field).createFilter())
		},
		[procedureDataFilterManager],
	)

	const handleProcedureStepFieldSelect = useCallback(
		(field: Field) => {
			if (!procedureDataFilterManager) return

			procedureDataFilterManager.addFilter(getFilterDefOfField(field).createFilter())
		},
		[procedureDataFilterManager],
	)

	const supportedProcedureTypeFields = useMemo(() => {
		if (!procedureTypeFieldSchema) return

		return procedureTypeFieldSchema.getFields()
	}, [procedureTypeFieldSchema])

	const supportedProcedureStepFields = useMemo(() => {
		if (!procedureStepFieldSchemasAndConditions) return

		const ret: Record<ProcedureStep["uuid"], Field[]> = {}

		for (const [uuid, [fields]] of Object.entries(procedureStepFieldSchemasAndConditions)) {
			ret[uuid] = fields.getFields()
		}

		return ret
	}, [procedureStepFieldSchemasAndConditions])

	return (
		<Menu.Root {...rest}>
			<Menu.ClickTrigger>{children}</Menu.ClickTrigger>
			<Menu.Content variant={variant} size={size} accentColor={accentColor}>
				<Menu.Scroll>
					<Menu.Item onSelect={handleSelectLabel}>
						<LuIcon icon={modelIconMapping.text} />
						Label
					</Menu.Item>

					<Menu.Item onSelect={handleSelectSubmittedAt}>
						<LuIcon icon={modelIconMapping.dueDate} />
						Submitted at
					</Menu.Item>

					{assetTypeStatuses.length > 0 && (
						<Menu.Item onSelect={handleSelectStatus}>
							<LuIcon icon={modelIconMapping.status} />
							Status
						</Menu.Item>
					)}

					{assetTypeIdentifiers.length > 0 && (
						<>
							<Menu.Label>Identifiers</Menu.Label>
							<IdentifierGroup
								identifiers={assetTypeIdentifiers}
								onIdentifierSelect={handleIdentifierSelect}
							/>
						</>
					)}

					{supportedAssetTypeFields.length > 0 && (
						<>
							<Menu.Label>Fields</Menu.Label>
							<FieldGroup fields={supportedAssetTypeFields} onFieldSelect={handleAssetTypeFieldSelect} />
						</>
					)}

					{!!supportedProcedureTypeFields && supportedProcedureTypeFields.length > 0 && (
						<>
							<Menu.Label>Procedure fields</Menu.Label>
							<FieldGroup
								fields={supportedProcedureTypeFields}
								onFieldSelect={handleProcedureTypeFieldSelect}
							/>
						</>
					)}

					{!!procedureSteps &&
						!!supportedProcedureStepFields &&
						procedureSteps.length > 0 &&
						procedureSteps.map((procedureStep) => {
							const fields = supportedProcedureStepFields[procedureStep.uuid]

							return (
								<Fragment key={procedureStep.uuid}>
									<Menu.Label>{procedureStep.name}</Menu.Label>
									<Menu.Item
										onSelect={() => handleSelectProcedureStepCompletionStatus(procedureStep)}
									>
										<LuIcon icon={modelIconMapping.status} />
										Completion
									</Menu.Item>
									<Menu.Item onSelect={() => handleSelectProcedureStepReviewStatus(procedureStep)}>
										<LuIcon icon={Eye} />
										Review
									</Menu.Item>
									<Menu.Item onSelect={() => handleSelectProcedureStepAssignees(procedureStep)}>
										<LuIcon icon={modelIconMapping.assignee} />
										Assignees
									</Menu.Item>
									<Menu.Item onSelect={() => handleSelectProcedureStepReviewers(procedureStep)}>
										<LuIcon icon={modelIconMapping.reviewer} />
										Reviewers
									</Menu.Item>
									{fields.length > 0 && (
										<FieldGroup fields={fields} onFieldSelect={handleProcedureStepFieldSelect} />
									)}
								</Fragment>
							)
						})}
				</Menu.Scroll>
			</Menu.Content>
		</Menu.Root>
	)
})
AssetDataMapFilterMenu.displayName = "AssetDataFilterMenu"
